/* eslint-disable no-console */
import React, { ReactNode, useEffect, useRef } from 'react'

import { Pagination } from '@egjs/flicking-plugins'
import Flicking, { FlickingOptions, FlickingProps, Plugin, ViewportSlot } from '@egjs/react-flicking'
import classNames from 'classnames'

import { useContent } from '@web/libs/contexts/ContentContext'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'

import styles from './FlickingArea.module.scss'
import useArrowPlugin, { ArrowOption } from './useArrow'
import useChangeAlign from './useChangeAlign'
import useInitFlickingWhenWindowHasWidth from './useInitFlickingWhenWindowHasWidth'
import usePaginationPlugin from './usePagination'
import useReInitializeOnNonStrict from './useReInitializeOnNonStrict'

type Props = {
  options?: Partial<FlickingProps & Omit<FlickingOptions, 'autoInit'>>
  arrowOption?: ArrowOption
  paginationType?: Pagination['type']
  children?: ReactNode
  currentIndex?: number
  alignLeftFocusCenter?: boolean
  className?: string
  useReinitialize?: boolean
  flickingCode?: string
}

/**
 * @link https://naver.github.io/egjs-flicking/Options
 */
const defaultOptions: Pick<FlickingOptions, 'moveType' | 'bound' | 'autoInit'> = {
  // FIXME: use MOVE_TYPE
  // MOVE_TYPE.FREE_SCROLL 사용시 Cannot read properties of undefined (reading 'FREE_SCROLL') 에러 발생
  moveType: 'freeScroll',
  bound: true,
  autoInit: false,
}
const FlickingArea: React.ForwardRefRenderFunction<Flicking, Props> = (
  {
    children,
    currentIndex,
    options,
    alignLeftFocusCenter,
    arrowOption,
    paginationType,
    className,
    useReinitialize = true,
    flickingCode,
  },
  forwardedRef,
) => {
  const content = useContent()

  const flickingOptions: Partial<FlickingProps> = {
    ...defaultOptions,
    ...options,
    onMoveEnd: e => {
      if (options?.onMoveEnd) options.onMoveEnd(e)

      if (flickingCode) {
        sendClickLog({ event: e, area: flickingCode, contentId: content.id, contentType: content.type })
      }
    },
  }
  const ref = useRef<Flicking>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const flickingRef = (forwardedRef ?? ref) as React.RefObject<Flicking>
  const [arrowPlugin, ArrowComponent] = useArrowPlugin(flickingOptions.moveType, arrowOption)
  const [paginationPlugin, PaginationComponent] = usePaginationPlugin(paginationType)
  const plugins = [arrowPlugin, paginationPlugin].filter(p => !!p) as Plugin[]

  useInitFlickingWhenWindowHasWidth(flickingRef)
  useChangeAlign(wrapperRef, flickingRef, alignLeftFocusCenter)
  useReInitializeOnNonStrict(flickingRef, children, useReinitialize)
  useEffect(() => {
    if (currentIndex !== undefined && flickingRef.current?.initialized) {
      flickingRef.current.moveTo(currentIndex).catch(() => null)
    }
  }, [flickingRef, currentIndex])

  return (
    <div ref={wrapperRef} className={classNames(styles.flicking_area, className)}>
      <Flicking ref={flickingRef} plugins={plugins} {...flickingOptions}>
        {children}
        <ViewportSlot>
          <ArrowComponent />
          <PaginationComponent />
        </ViewportSlot>
      </Flicking>
    </div>
  )
}

export default React.forwardRef(FlickingArea)
